import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Plus, ListChecks, Sparkles, Loader2, CheckCircle2, HelpCircle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import HfixActionDialog from "@/components/hfacs/HfixActionDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTION_STATUSES = ["À faire", "En cours", "Réalisée", "Vérifiée efficace"];
const STATUS_STYLES = {
  "À faire": "bg-slate-100 text-slate-700 border-slate-300",
  "En cours": "bg-amber-500/15 text-amber-700 border-amber-500/30",
  "Réalisée": "bg-sky-500/15 text-sky-700 border-sky-500/30",
  "Vérifiée efficace": "bg-emerald-500/20 text-emerald-700 border-emerald-500/40 font-semibold",
};
const VERDICT_STYLES = {
  "Cohérente": { icon: CheckCircle2, cls: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30" },
  "À préciser": { icon: HelpCircle, cls: "bg-amber-500/15 text-amber-700 border-amber-500/30" },
  "Incohérente": { icon: XCircle, cls: "bg-rose-500/15 text-rose-700 border-rose-500/30" },
};

function scoreColor(score) {
  if (score >= 20) return "text-emerald-600";
  if (score >= 13) return "text-amber-600";
  return "text-rose-600";
}

export default function HfixTab({ analysisId }) {
  const { user } = useAuth();
  const canUseCopilot = user?.role === "Administrateur national";
  const [actions, setActions] = useState([]);
  const [paths, setPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [copilotResult, setCopilotResult] = useState(null);
  const [copilotLoading, setCopilotLoading] = useState(false);
  const [initialValues, setInitialValues] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, p] = await Promise.all([
        axios.get(`${API}/hfacs/${analysisId}/actions`),
        axios.get(`${API}/hfacs/${analysisId}/critical-paths`),
      ]);
      setActions(a.data.sort((x, y) => Number(y.SCORE_HFIX || 0) - Number(x.SCORE_HFIX || 0)));
      setPaths(p.data);
    } catch (e) {
      toast.error("Impossible de charger le plan HFIX.");
    } finally {
      setLoading(false);
    }
  }, [analysisId]);

  useEffect(() => { load(); }, [load]);

  const createAction = async (payload) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/hfacs/${analysisId}/actions`, payload);
      toast.success("Action HFIX créée.");
      setDialogOpen(false);
      setInitialValues(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la création de l'action.");
    } finally {
      setSubmitting(false);
    }
  };

  const runCopilot = async () => {
    setCopilotLoading(true);
    setCopilotResult(null);
    try {
      const { data } = await axios.post(`${API}/hfacs/${analysisId}/hfix-copilot`);
      setCopilotResult(data);
      if ((data.coherence || []).length === 0 && (data.nouvelles_actions || []).length === 0) {
        toast.info("Le Copilote IA n'a rien à signaler pour l'instant.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'analyse du Copilote IA.");
    } finally {
      setCopilotLoading(false);
    }
  };

  const openWithSuggestion = (s) => {
    setInitialValues({
      description_action: s.description_action || "", cause_hfacs_associee: s.cause_hfacs_associee || "",
      id_chemin: s.id_chemin || "",
      faisabilite: s.faisabilite || 3, acceptabilite: s.acceptabilite || 3, couts_vs_avantages: s.couts_vs_avantages || 3,
      efficacite: s.efficacite || 3, durabilite: s.durabilite || 3,
    });
    setDialogOpen(true);
  };

  const verdictFor = (actionId) => (copilotResult?.coherence || []).find((c) => c.id_action === actionId);

  const updateStatus = async (action_id, statut) => {
    try {
      await axios.put(`${API}/hfacs/${analysisId}/actions/${action_id}`, { statut });
      load();
    } catch (e) {
      toast.error("Erreur lors de la mise à jour de l'action.");
    }
  };

  const pathName = (id) => paths.find((p) => p.ID_CHEMIN === id)?.NOM_CHEMIN;

  return (
    <div>
      <div className="mb-4 px-4 py-3 rounded-sm border border-cyan-500/30 bg-cyan-500/5 text-xs text-cyan-700">
        Chaque action est cotée sur 5 critères (1 à 5) pour un score total sur 25 : Faisabilité, Acceptabilité,
        Coûts vs avantages, Efficacité, Durabilité. Reliez si possible l'action à un chemin critique validé.
      </div>
      <Button onClick={() => setDialogOpen(true)} data-testid="hfix-add-action-button" className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2 mb-3">
        <Plus className="h-4 w-4" /> Ajouter une action HFIX
      </Button>

      {canUseCopilot && (
        <div className="mb-6">
          <Button
            onClick={runCopilot}
            disabled={copilotLoading}
            data-testid="hfix-copilot-analyze-button"
            variant="outline"
            className="gap-2 border-accent/40 text-accent hover:bg-accent/10"
          >
            {copilotLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Analyser avec le Copilote IA
          </Button>

          {copilotResult && (copilotResult.nouvelles_actions || []).length > 0 && (
            <div data-testid="hfix-copilot-suggestions" className="mt-4 space-y-3">
              <p className="font-mono text-xs uppercase text-accent">Actions complémentaires suggérées par l'IA</p>
              {copilotResult.nouvelles_actions.map((s, i) => (
                <div key={i} data-testid={`hfix-copilot-suggestion-${i}`} className="bg-accent/5 border border-accent/30 rounded-md p-4">
                  <p className="text-sm font-medium leading-snug mb-1">{s.description_action}</p>
                  {s.justification && <p className="text-xs text-muted-foreground mb-2">{s.justification}</p>}
                  {s.id_chemin && pathName(s.id_chemin) && <p className="text-xs text-muted-foreground mb-3">Chemin visé : {pathName(s.id_chemin)}</p>}
                  <Button size="sm" onClick={() => openWithSuggestion(s)} data-testid={`hfix-copilot-suggestion-add-${i}`} className="bg-accent hover:bg-accent/90 text-accent-foreground">
                    Ajouter cette action
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Chargement…</p>
      ) : (
        <div className="space-y-3" data-testid="hfix-actions-list">
          {actions.map((a) => {
            const verdict = verdictFor(a.ID_ACTION);
            const VerdictIcon = verdict ? (VERDICT_STYLES[verdict.verdict]?.icon || HelpCircle) : null;
            return (
            <div key={a.ID_ACTION} data-testid={`hfix-action-row-${a.ID_ACTION}`} className="bg-card border border-border rounded-md p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                <div className="flex items-start gap-2 flex-1">
                  <ListChecks className="h-4 w-4 text-cyan-600 mt-0.5" />
                  <p className="text-sm font-medium leading-snug">{a.DESCRIPTION_ACTION}</p>
                </div>
                <span data-testid={`hfix-action-score-${a.ID_ACTION}`} className={`font-heading font-black text-lg ${scoreColor(Number(a.SCORE_HFIX || 0))}`}>
                  {a.SCORE_HFIX} / 25
                </span>
              </div>
              {a.ID_CHEMIN && pathName(a.ID_CHEMIN) && (
                <p className="text-xs text-muted-foreground mb-2">Chemin lié : {pathName(a.ID_CHEMIN)}</p>
              )}
              {verdict && (
                <div data-testid={`hfix-copilot-verdict-${a.ID_ACTION}`} className={`flex items-start gap-2 text-xs rounded-sm border px-2 py-1.5 mb-2 ${VERDICT_STYLES[verdict.verdict]?.cls || "bg-slate-100 text-slate-700 border-slate-300"}`}>
                  <VerdictIcon className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span><strong>Copilote IA — {verdict.verdict} :</strong> {verdict.commentaire}</span>
                </div>
              )}
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs text-muted-foreground flex gap-3 flex-wrap">
                  {a.RESPONSABLE && <span>Responsable : {a.RESPONSABLE}</span>}
                  {a.ECHEANCE && <span>Échéance : {a.ECHEANCE}</span>}
                </div>
                <select
                  data-testid={`hfix-action-status-select-${a.ID_ACTION}`}
                  value={a.STATUT}
                  onChange={(e) => updateStatus(a.ID_ACTION, e.target.value)}
                  className={`font-mono text-[11px] uppercase px-2 py-1 rounded-sm border ${STATUS_STYLES[a.STATUT] || ""}`}
                >
                  {ACTION_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            );
          })}
          {actions.length === 0 && (
            <p className="text-sm text-muted-foreground">Aucune action HFIX pour le moment.</p>
          )}
        </div>
      )}

      <HfixActionDialog
        open={dialogOpen}
        onOpenChange={(v) => { setDialogOpen(v); if (!v) setInitialValues(null); }}
        paths={paths}
        onSubmit={createAction}
        submitting={submitting}
        initialValues={initialValues}
      />
    </div>
  );
}
